import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff, Star, Building2, Upload } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const emptyForm = {
  name: '',
  nameEn: '',
  description: '',
  logo: '',
  website: '',
  active: true,
  featured: false,
  order: 0,
};

const ClientsAdmin = () => {
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadClients = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/clients/admin');
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('تعذر تحميل شركاء النجاح');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const openCreate = () => {
    setEditing('new');
    setForm(emptyForm);
  };

  const openEdit = (client) => {
    setEditing(client._id);
    setForm({
      name: client.name || '',
      nameEn: client.nameEn || '',
      description: client.description || '',
      logo: client.logo || '',
      website: client.website || '',
      active: Boolean(client.active),
      featured: Boolean(client.featured),
      order: Number(client.order || 0),
    });
  };

  const closeEditor = () => {
    if (saving) return;
    setEditing(null);
    setForm(emptyForm);
  };

  const handleLogoFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('اختاري ملف صورة');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('حجم الشعار يجب ألا يتجاوز 2 ميجابايت');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveClient = async () => {
    if (!form.name.trim()) {
      toast.error('اسم الشركة مطلوب');
      return;
    }

    const payload = {
      ...form,
      order: Number(form.order || 0),
      active: Boolean(form.active),
      featured: Boolean(form.featured),
    };

    try {
      setSaving(true);
      if (editing === 'new') {
        await api.post('/clients', payload);
        toast.success('تمت إضافة شريك النجاح');
      } else {
        await api.put(`/clients/${editing}`, payload);
        toast.success('تم تعديل شريك النجاح');
      }
      closeEditor();
      await loadClients();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'تعذر حفظ شريك النجاح');
    } finally {
      setSaving(false);
    }
  };

  const deleteClient = async (client) => {
    if (!window.confirm(`هل تريد حذف "${client.name}"؟`)) return;
    try {
      await api.delete(`/clients/${client._id}`);
      toast.success('تم حذف شريك النجاح');
      await loadClients();
    } catch (error) {
      toast.error('تعذر حذف شريك النجاح');
    }
  };

  const toggleActive = async (client) => {
    try {
      const { data } = await api.put(`/clients/${client._id}`, { active: !client.active });
      setClients(prev => prev.map(c => c._id === client._id ? data : c));
      toast.success(data.active ? 'تم إظهار شريك النجاح في الموقع' : 'تم إخفاء شريك النجاح من الموقع');
    } catch (error) {
      toast.error('تعذر تحديث حالة شريك النجاح');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">إدارة شركاء النجاح</h1>
          <p className="text-gray-500 text-sm mt-1">إدارة شركاء النجاح الذين تشرفنا بالعمل معهم وإظهارهم في الموقع الخارجي.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary inline-flex items-center justify-center gap-2">
          <Plus size={18} /> إضافة شريك نجاح
        </button>
      </div>

      {loading ? (
        <div className="glass p-10 text-center text-gray-400">جارٍ تحميل العملاء...</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {clients.map(client => (
            <div key={client._id} className="glass p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                  {client.logo ? (
                    <img src={client.logo} alt={client.name} className="w-full h-full object-contain p-2" />
                  ) : (
                    <Building2 className="text-orvix-green" size={32} />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => toggleActive(client)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs ${
                    client.active
                      ? 'bg-green-500/10 border-green-500/20 text-green-400'
                      : 'bg-gray-500/10 border-gray-500/20 text-gray-400'
                  }`}
                >
                  {client.active ? <Eye size={13} /> : <EyeOff size={13} />}
                  {client.active ? 'ظاهر بالموقع' : 'مخفي'}
                </button>
              </div>

              <div className="mt-5">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-white">{client.name}</h3>
                  {client.featured && <Star size={16} className="text-yellow-400 fill-yellow-400" />}
                </div>
                {client.nameEn && <p dir="ltr" className="text-xs text-gray-500 mt-1 text-right">{client.nameEn}</p>}
                <p className="text-gray-400 text-sm mt-3 min-h-[40px]">{client.description || 'بدون وصف'}</p>
                <div className="mt-4 text-xs text-gray-500">الترتيب: {client.order || 0}</div>
              </div>

              <div className="flex gap-2 mt-5 pt-4 border-t border-white/10">
                <button type="button" onClick={() => openEdit(client)} className="flex-1 px-3 py-2 rounded-lg bg-orvix-primary/15 text-orvix-primary hover:bg-orvix-primary/25 inline-flex items-center justify-center gap-2 text-sm">
                  <Pencil size={15} /> تعديل
                </button>
                <button type="button" onClick={() => deleteClient(client)} className="px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 inline-flex items-center gap-2 text-sm">
                  <Trash2 size={15} /> حذف
                </button>
              </div>
            </div>
          ))}

          {!clients.length && (
            <div className="glass p-10 text-center text-gray-400 md:col-span-2 xl:col-span-3">
              لا يوجد شركاء نجاح بعد. أضيفي أول شريك نجاح من الزر بالأعلى.
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeEditor}>
          <div className="glass w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 sm:p-8 relative" onClick={e => e.stopPropagation()}>
            <button type="button" onClick={closeEditor} className="absolute left-5 top-5 text-gray-400 hover:text-white">
              <X size={22} />
            </button>

            <h2 className="text-xl font-bold text-white mb-7">
              {editing === 'new' ? 'إضافة شريك نجاح' : 'تعديل شريك النجاح'}
            </h2>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-300 text-sm mb-2">اسم شريك النجاح بالعربي *</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">اسم شريك النجاح بالإنجليزي</label>
                <input dir="ltr" value={form.nameEn} onChange={e => setForm({...form, nameEn: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-300 text-sm mb-2">نبذة قصيرة</label>
                <textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none resize-none" />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">موقع الشركة</label>
                <input dir="ltr" value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://..." className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">الترتيب</label>
                <input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-300 text-sm mb-2">شعار الشركة</label>
                <div className="grid sm:grid-cols-[140px_1fr] gap-4 items-center">
                  <div className="w-32 h-32 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                    {form.logo ? <img src={form.logo} alt="Logo preview" className="w-full h-full object-contain p-2" /> : <Building2 size={38} className="text-gray-500" />}
                  </div>
                  <div className="space-y-3">
                    <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-200 cursor-pointer hover:bg-white/10">
                      <Upload size={16} /> اختيار صورة الشعار
                      <input type="file" accept="image/*" className="hidden" onChange={e => handleLogoFile(e.target.files?.[0])} />
                    </label>
                    <input dir="ltr" value={form.logo.startsWith('data:') ? '' : form.logo} onChange={e => setForm({...form, logo: e.target.value})} placeholder="أو رابط صورة الشعار" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                    {form.logo && <button type="button" onClick={() => setForm({...form, logo: ''})} className="text-xs text-red-400">إزالة الشعار</button>}
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} className="w-4 h-4 accent-emerald-500" />
                <div><div className="text-white text-sm font-medium">إظهار في الموقع</div><div className="text-xs text-gray-500 mt-1">يمكن إخفاء العميل مؤقتاً.</div></div>
              </label>

              <label className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} className="w-4 h-4 accent-yellow-500" />
                <div><div className="text-white text-sm font-medium">عميل مميز</div><div className="text-xs text-gray-500 mt-1">يظهر أولاً في قسم العملاء.</div></div>
              </label>
            </div>

            <div className="flex gap-3 justify-end mt-7 pt-5 border-t border-white/10">
              <button type="button" onClick={closeEditor} className="px-5 py-2.5 rounded-xl bg-white/5 text-gray-300 hover:bg-white/10">إلغاء</button>
              <button type="button" disabled={saving} onClick={saveClient} className="btn-primary inline-flex items-center gap-2">
                <Save size={17} /> {saving ? 'جاري الحفظ...' : 'حفظ العميل'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsAdmin;
